<template>
    <mini-calculator-page>
        <div slot="summary-panel">
            <div class="p-animated-calc__results a-form-inputs u-full-width u-margin-auto">
                <mini-calculator-header title="Care Calculator" icon="care-calculator"/>

                <care-period v-for="(period, index) in periods"
                             :index="index" :period="period"></care-period>

                <button class="a-button a-button--smaller"
                        @click="addCarePeriod">
                    Add Care Period
                </button>

                <h2>
                    Result
                </h2>

                <control for="sub_total" label="Total"
                         before-after="pound-on-dark">
                    <input autocomplete="off" :value="currencyFormat(total, '')" type="text" disabled>
                </control>
            </div>
        </div>
        <div slot="results-panel">
            <div class="p-animated-calc__results-panel u-full-width u-margin-auto" v-show="showExplanation">
                <modal name="care-rates" :noDefaultButton="true">
                    <h1>
                        Care and Attendance Rates
                    </h1>
                    <table v-if="!isLoading()" class="a-table a-table--full-width ">
                        <thead>
                        <tr>
                            <th>Date</th>
                            <th>Basic</th>
                            <th>Aggregate</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(careRate, date) in rates">
                            <td>{{ formatModalDate(date) }}</td>
                            <td>{{ careRate.basic }}</td>
                            <td>{{ careRate.aggregate }}</td>
                        </tr>
                        </tbody>
                    </table>
                </modal>

                <h2>Explanation</h2>

                <div v-for="(period, index) in periods">
                    <h2>Period {{ index + 1 }}</h2>

                    <p>
                        The total cost of care for the period between <strong>{{getPeriodStart(period)}}</strong> and
                        <strong>{{getPeriodEnd(period)}}</strong>, at the <strong>{{period.rate}}</strong> rate, for
                        <strong>{{period.hoursPerDay}} hour{{period.hoursPerDay === 1 ? '' : 's'}}</strong> per day has
                        been calculated to be <strong>{{currencyFormat(period.total)}}</strong>.
                    </p>

                    <p>
                        This has been calculated using the formula:
                    </p>

                    <div class="m-formula">
                        <div class="m-formula__item--row">
                            <strong>
                                [Total Hours] x [Care Rate] = [Total Cost of Care]
                            </strong>
                        </div>
                    </div>

                    <!-- When care only spans one rate -->
                    <p v-if="period.table[0].length == 1" v-for="table in period.table[0]">
                        Where the total number of hours is <strong>{{table.totalHours}}</strong> and the
                        {{ period.rate }} care rate is taken as
                        <strong>{{ currencyFormat(table.rates[period.rate]) }}</strong>.
                    </p>

                    <!-- When care spans multiple periods -->
                    <p v-if="period.table[0].length > 1">
                        As the care period spans multiple recommended rates for care and attendance, each period must be broken
                        down and totalled up individually using the appropriate rate for each period. You can view these rates
                        by clicking <a href="#" @click.prevent="toggleCareRatesModal()">here</a>.
                    </p>

                    <p v-if="period.gratuitousCare">
                        Since you have specified that the care provided was gratuitous (provided by a family member or friend
                        without charge), a discount of 25% must be applied to the above total figure. The resulting total cost
                        of care is therefore: <strong>{{currencyFormat(period.total)}}</strong>
                    </p>

                    <div class="a-table a-table--short-no-style a-table--full-width">
                        <table v-if="period.table[0].length > 0">
                            <thead>
                            <tr>
                                <th>
                                    Period Start
                                </th>
                                <th>
                                    Period End
                                </th>
                                <th>
                                    Total Days
                                </th>
                                <th>
                                    Total Hours
                                </th>
                                <th>
                                    {{ period.rate.charAt(0).toUpperCase() + period.rate.slice(1) }} Rate
                                </th>
                                <th>
                                    Total
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="table in period.table[0]">
                                <td>{{formatDate(table.start.date)}}</td>
                                <td>{{formatDate(table.end.date)}}</td>
                                <td>{{table.totalDays}}</td>
                                <td>{{table.totalHours}}</td>
                                <td>
                                    &pound;{{twoDp(!period.gratuitousCare ? table.rates[period.rate] : table.rates[period.rate] * 0.75)}}
                                </td>
                                <td>
                                    {{currencyFormat(table.total)}}
                                </td>
                            </tr>
                            </tbody>
                            <tfoot>
                            <tr>
                                <td colspan="5">

                                </td>
                                <td>
                                    {{currencyFormat(period.total)}}
                                </td>
                            </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </mini-calculator-page>
</template>

<script type="text/babel">
    import {mapGetters, mapActions} from 'vuex';
    import Control from 'components/form/Control.vue';
    import ControlError from 'components/form/control/Error.vue';
    import FormValidator from 'components/form/validator/mixin';
    import MiniCalculatorPage from 'pages/mini-calculators/components/MiniCalculatorPage';
    import Intro from 'pages/mini-calculators/calculators/CalculatorComponents/Intro';
    import modal from 'components/modal/Modal';
    import miniCalcIcons from 'components/Icons';
    import DateInput from 'components/form/control/DateInput.vue';
    import currencyFormat from 'mixins/currencyFormat';
    import CalculatorKeyBindings from 'pages/mini-calculators/calculators/mixins/MiniCalculatorBindings';
    import MiniCalculatorHeader from 'pages/mini-calculators/components/MiniCalculatorHeader';
    import moment from 'moment';
    import CarePeriod from 'pages/mini-calculators/components/CarePeriod';
    import {validRequest} from 'store/modules/mini/care';

    export default {
        name: 'care-calculator',
        created() {
            this.resetForm();
            this.loadCareRates();
        },
        computed: {
            ...mapGetters('mini/care', {detailValue: 'value' }),
            ...mapGetters('http', ['apiRouteRequestRunning']),
            periods: {
                get() {
                    return this.$store.getters['mini/care/periods']
                },
                set(value) {
                    this.updateDetail({key: 'periods', value});
                }
            },
            valid(){
                return !!this.periods.reduce((carry, period) => {
                    return carry ? validRequest(period) : carry;
                }, 1);
            },
            totalHours() {
                return this.periods.reduce(function (carry, period) {
                    return carry + period.hours;
                }, 0);
            },
            total() {
                return this.periods.reduce(function (carry, period) {
                    return carry + period.total;
                }, 0);
            },
            rates: {
                get() {
                    return this.detailValue('rates');
                },
                set(value) {
                    this.updateDetail({key: 'rates', value});
                }
            },
            showExplanation() {
                return this.valid && this.total > 0 && this.periods.reduce((valid, period) => !valid ? valid : period.total > 0)
            }
        },
        watch:{
            valid() {
                this.updatePrompt();
            },
            total() {
                this.updatePrompt();
            }
        },
        mouted(){
            this.hidePrompt();
        },
        mixins: [
            FormValidator,
            currencyFormat,
            CalculatorKeyBindings
        ],
        components: {
            Control,
            ControlError,
            DateInput,
            MiniCalculatorPage,
            MiniCalculatorHeader,
            miniCalcIcons,
            Intro,
            CarePeriod,
            modal
        },
        methods: {
            ...mapActions('modal', ['toggle']),
            ...mapActions('mini/care', {
                updateDetail: 'update',
                loadCareRates: 'loadCareRates',
                addPeriod: 'addPeriod',
                resetForm: 'reset'
            }),
            diffInDays(table) {
                return moment(table.end.date).diff(moment(table.start.date), 'days');
            },
            twoDp(value) {
                return (Math.round(value * 100) / 100).toFixed(2);
            },
            formatDate(date) {
                if (!date) {
                    return 'N/A';
                }

                return moment(date).format('DD/MM/YYYY')
            },
            isLoading() {
                return this.apiRouteRequestRunning('/api/v1/mini-calculators/care/find-periods');
            },
            addCarePeriod() {
                this.addPeriod();
            },
            toggleCareRatesModal() {
                this.toggle('care-rates');
            },
            formatModalDate(date) {
                return moment(date, 'YYYY-MM-DD').format('DD/MM/YYYY')
            },
            getPeriodStart(period) {
                if (!period.includeFirstDay) {
                    return moment(period.periodStart, 'DD/MM/YYYY').add(1, 'day').format('DD/MM/YYYY');
                }
                return period.periodStart;
            },
            getPeriodEnd(period) {
                if (!period.includeLastDay) {
                    return moment(period.periodEnd, 'DD/MM/YYYY').subtract(1, 'day').format('DD/MM/YYYY');
                }
                return period.periodEnd;
            },
            updatePrompt(){
                if (this.showExplanation) {
                    this.hidePrompt();
                } else {
                    this.showPrompt();
                }
            }
        }
    }
</script>



// WEBPACK FOOTER //
// careCalculator.vue?39c1c5dc